// React Core
import { useEffect, useState, useRef, Suspense, type ReactNode } from "react";
// Redux Toolkit
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "./store/store";
// React Router Dom
//@ts-ignore
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from "react-router-dom";
// Services
import { FilterLeakedSites, isAuth } from "./services/common";
import TokenService, { ACCESS_TOKEN_CHANGED_EVENT } from "./services/autentication/token.service";

// CSS start
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/md-dark-indigo/theme.css";
import "primereact/resources/primereact.css";
import "primeflex/primeflex.css";

import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import "./App.css";
import "./assets/css/menu.css";
// import "./assets/css/inspection-overview/mapview.css";
import "./assets/css/primereact-overrides/common-style.css";

// Access Mode
import { PermissionsProvider } from "./providers/PermissionsProvider";
// AURA 2.0 start
import MenuSidebar from "./components/common/menu/MenuSidebar";
import SurveySidebar from "./components/inspection-overview/site-operations/survey-details/SurveySidebar";
import HeaderComponent from "./components/common/header/HeaderComponent";
import OverviewSitesService from "./services/overviewmapsites/overviewmapsites";
import { ProgressSpinner } from "primereact/progressspinner";
import UserService from "./services/user/user.service";
import { UserSettingsReducer } from "./store/user/user.store";
import { setLeakedSites, setSelectedCustomerInfo } from "./store/common";
import { SiteInfoReducer } from "./store/inspection-overview/inspection-overview.store";
import {
  ensureAllVoluntarySurveysLoaded,
} from "./store/voluntary-survey/voluntary-survey.store";
import { MapTableStyles } from "./constants/common-styles.constants";
import VSurveyHistory from "./components/voluntary-surveys/survey-details/VSurveyHistory";
import Tabs from "./constants/tabs.constants";
import {
  LazyAuraDashboard,
  LazyCameraOperator,
  LazyListView,
  LazyMapView,
  LazyPinned,
  LazyPinnedSitesTable,
  LazySiteOperationsMain,
  LazySitesTable,
  LazySiteSummary,
  LazyUnderConstruction,
  LazyVoluntaryMapView,
  LazyVSurveySiteOperationsMain,
} from "./app/lazyRoutes";
import Signout from "./components/signout/SignoutComponent";
import User from "./components/user/UserComponent";

const mainSiteUrl = process.env.REACT_APP_MVC_URL;

interface SiteDropdown {
  name: string;
  code: string;
  cameraSiteId: string;
  lastSurveyDate: string;
  nextSurveyDate: any,
  latitude: any,
  longitude: any
}
interface HeaderInfoType {
  selectedSite?: SiteDropdown;
  selectedCustomer?: any;
  lastSurveyDate?: any;
  nextSurveyDate?: any;
  cameraSiteId?: any;
}
interface CustomerDropdown {
  name: string;
  code: string;
  customerId: string;
}

/** Keeps tab visibility and menu labels in sync with the URL (App does not re-render on navigation). */
function RouteSync({
  setTabDisplaytoggle,
  setSelectedMenu,
}: {
  setTabDisplaytoggle: (v: boolean) => void;
  setSelectedMenu: (v: string) => void;
}) {
  const { pathname } = useLocation();
  useEffect(() => {
    const isTabDisplay =
      pathname.startsWith("/regulatory-surveys") ||
      pathname.startsWith("/voluntary-surveys") ||
      pathname.startsWith("/asset-summary");
    setTabDisplaytoggle(isTabDisplay);

    if (pathname === "/" || pathname === "" || pathname === "Home") {
      setSelectedMenu("Home");
    } else if (pathname === "/regulatory-surveys" || pathname === "/regulatory-surveys/mapview") {
      setSelectedMenu("Map view regulatory");
    } else if (pathname === "/regulatory-surveys/listview") {
      setSelectedMenu("List view regulatory");
    } else if (pathname === "/regulatory-surveys/pinned") {
      setSelectedMenu("Pinned regulatory");
    } else if (
      pathname === "/sites-summary" ||
      pathname === "/camera-operator" ||
      pathname === "/asset-summary" ||
      pathname.startsWith("/asset-summary/")
    ) {
      setSelectedMenu("Asset Summary");
    } else if (pathname === "/voluntary-surveys" || pathname === "/voluntary-surveys/mapview") {
      setSelectedMenu("Map view voluntary");
    } else if (pathname === "/voluntary-surveys/listview") {
      setSelectedMenu("List view voluntary");
    } else if (pathname === "/voluntary-surveys/pinned") {
      setSelectedMenu("Pinned voluntary");
    } else if (pathname === "/regulatory-details") {
      setSelectedMenu("Details of regulatory");
    } else if (pathname === "/voluntary-details") {
      setSelectedMenu("Details of voluntary");
    } else if (pathname === "/reports") {
      setSelectedMenu("Reports");
    }
  }, [pathname, setTabDisplaytoggle, setSelectedMenu]);
  return null;
}

/** Survey / asset tab strip — must read `useLocation` here so links and badges update on client navigation. */
function SurveyTabsBar(props: {
  tabDisplaytoggle: boolean;
  selectedMenu: string;
  setSelectedMenu: (v: string) => void;
  pinndataLength?: number;
  siteDataLength?: number;
  pinndataVoluntaryLength?: number;
  listVoluntaryLength?: number;
}) {
  const { pathname } = useLocation();
  if (!props.tabDisplaytoggle) return null;

  const tabItems = pathname.startsWith("/asset-summary")
    ? Tabs.dataPreferences
    : pathname.startsWith("/voluntary-surveys")
      ? Tabs.voluntarySurvey
      : pathname.startsWith("/regulatory-surveys")
        ? Tabs.regulatorySurvey
        : [];

  const getTabMenu = (tab: any) => {
    const lbl = tab.label;
    const pathUrl = pathname;
    return (
      <>
        {lbl.includes("Pinned") && pathUrl.startsWith("/voluntary-surveys") ? (
          <>
            <span>Pinned</span>
            <button className="count-badge">{props.pinndataVoluntaryLength}</button>
          </>
        ) : lbl.includes("Pinned") && pathUrl.startsWith("/regulatory-surveys") ? (
          <>
            <span>Pinned</span>
            <button className="count-badge">{props.pinndataLength}</button>
          </>
        ) : lbl.includes("List view") && pathUrl.startsWith("/voluntary-surveys") ? (
          <>
            <span>List view</span>
            <button className="count-badge">{props.listVoluntaryLength}</button>
          </>
        ) : lbl.includes("List view") && pathUrl.startsWith("/regulatory-surveys") ? (
          <>
            <span>List view</span>
            <button className="count-badge">{props.siteDataLength}</button>
          </>
        ) : lbl.includes("Map view") && pathUrl.startsWith("/voluntary-surveys") ? (
          <>
            <span>Map view</span>
          </>
        ) : lbl.includes("Pinned") ? (
          <>
            <span>Pinned</span>
            <button className="count-badge">{props.pinndataLength}</button>
          </>
        ) : (
          lbl
        )}
      </>
    );
  };

  const getTabLinkClassName = (tab: any) => {
    if (pathname.startsWith("/asset-summary")) {
      const p = pathname.replace(/\/+$/, "") || "/";
      const siteTab =
        tab.url === "/asset-summary/sites-summary" || tab.label === "Site summary";
      const cameraTab =
        tab.url === "/asset-summary/camera-operator" || tab.label === "Camera operator";
      const siteActive =
        siteTab &&
        (p === "/asset-summary" ||
          p === "/asset-summary/sites-summary" ||
          p === "/sites-summary");
      const cameraActive =
        cameraTab &&
        (p === "/asset-summary/camera-operator" || p === "/camera-operator");
      const isActive = siteActive || cameraActive;
      return `tabmenu-item ${isActive ? "active" : ""}`;
    }

    const isActiveCls =
      props.selectedMenu.includes(tab.label) ||
      (props.selectedMenu === "Voluntary Surveys" && tab.label === "Map view voluntary");
    return `tabmenu-item ${isActiveCls ? "active" : ""}`;
  };

  return (
    <div className="tabmenu-container">
      {tabItems.map((item: any) => (
        <Link
          key={item.label}
          to={item.url}
          className={getTabLinkClassName(item)}
          onClick={() => props.setSelectedMenu(item.label)}
        >
          {getTabMenu(item)}
        </Link>
      ))}
    </div>
  );
}

type KeyedAppRoutesProps = {
  updateHeaderDataFn: (rowData: any) => void;
  getSelectedMenu: (menu: any) => void;
  setTabDisplaytoggle: (v: boolean) => void;
  selectedValue: string;
  selectedHeaderInfo: HeaderInfoType;
  updatePinnedDataLength: (length: number) => void;
  updateVoluntaryPinnedData: (length: number) => void;
  handleSelectedSiteName: (siteName: any) => void;
};

/**
 * Remount route content when the URL changes (same component type on multiple paths).
 * Use a real DOM node (not Fragment): Google Maps / LoadScript mutates the tree; a keyed Fragment
 * can cause "removeChild: node is not a child" when React unmounts during navigation.
 */
function AppRouteMount({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  /** Include `search` so detail routes remount when only query params change (same pathname). */
  return (
    <div key={`${pathname}${search}`} className="app-route-mount">
      {children}
    </div>
  );
}

function KeyedAppRoutes(props: KeyedAppRoutesProps) {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <AppRouteMount>
            <LazyAuraDashboard
              setTabDisplaytoggle={props.setTabDisplaytoggle}
              updateSelectedMenu={props.getSelectedMenu}
              updateHeaderData={props.updateHeaderDataFn}
            />
          </AppRouteMount>
        }
      />

      <Route
        path="/regulatory-surveys"
        element={
          <AppRouteMount>
            <LazyMapView updateHeaderData={props.updateHeaderDataFn} />
          </AppRouteMount>
        }
      />
      <Route
        path="/regulatory-surveys/mapview"
        element={
          <AppRouteMount>
            <LazyMapView updateHeaderData={props.updateHeaderDataFn} />
          </AppRouteMount>
        }
      />
      <Route
        path="/regulatory-surveys/listview"
        element={
          <AppRouteMount>
            <LazySitesTable
              updateHeaderData={props.updateHeaderDataFn}
              updateSiteDataLength={props.updatePinnedDataLength}
            />
          </AppRouteMount>
        }
      />
      <Route
        path="/regulatory-surveys/pinned"
        element={
          <AppRouteMount>
            <LazyPinnedSitesTable
              updateHeaderData={props.updateHeaderDataFn}
              updatePinnedDataLength={props.updatePinnedDataLength}
            />
          </AppRouteMount>
        }
      />
      <Route
        path="/regulatory-details"
        element={
          <AppRouteMount>
            <LazySiteOperationsMain
              selectedValue={props.selectedValue}
              selectedHeaderInfo={props.selectedHeaderInfo}
            />
          </AppRouteMount>
        }
      />
      <Route
        path="/asset-summary"
        element={
          <AppRouteMount>
            <LazySiteSummary name={`Asset Summary`} />
          </AppRouteMount>
        }
      />
      <Route
        path="/asset-summary/sites-summary"
        element={
          <AppRouteMount>
            <LazySiteSummary name={`Asset Summary`} />
          </AppRouteMount>
        }
      />
      <Route
        path="/asset-summary/camera-operator"
        element={
          <AppRouteMount>
            <LazyCameraOperator />
          </AppRouteMount>
        }
      />

      <Route
        path="/voluntary-surveys"
        element={
          <AppRouteMount>
            <LazyVoluntaryMapView updateHeaderData={props.updateHeaderDataFn} />
          </AppRouteMount>
        }
      />
      <Route
        path="/voluntary-surveys/mapview"
        element={
          <AppRouteMount>
            <LazyVoluntaryMapView updateHeaderData={props.updateHeaderDataFn} />
          </AppRouteMount>
        }
      />
      <Route
        path="/voluntary-surveys/listview"
        element={
          <AppRouteMount>
            <LazyListView
              updateHeaderData={props.updateHeaderDataFn}
              updateListDataLength={props.updateVoluntaryPinnedData}
              onSiteSelect={props.handleSelectedSiteName}
            />
          </AppRouteMount>
        }
      />
      <Route
        path="/voluntary-surveys/pinned"
        element={
          <AppRouteMount>
            <LazyPinned
              updateHeaderData={props.updateHeaderDataFn}
              updateListDataLength={props.updateVoluntaryPinnedData}
            />
          </AppRouteMount>
        }
      />
      <Route
        path="/voluntary-details"
        element={
          <AppRouteMount>
            <LazyVSurveySiteOperationsMain
              selectedValue={props.selectedValue}
              selectedHeaderInfo={props.selectedHeaderInfo}
            />
          </AppRouteMount>
        }
      />

      <Route
        path="/reports"
        element={
          <AppRouteMount>
            <LazyUnderConstruction name={`Reports`} />
          </AppRouteMount>
        }
      />

      <Route
        path="/user"
        element={
          <AppRouteMount>
            <LazyUnderConstruction name={`User`} />
          </AppRouteMount>
        }
      />
      <Route
        path="/signout"
        element={
          <AppRouteMount>
            <LazyUnderConstruction name={`Signout`} />
          </AppRouteMount>
        }
      />
      <Route
        path="*"
        element={
          <AppRouteMount>
            <LazyUnderConstruction />
          </AppRouteMount>
        }
      />
    </Routes>
  );
}

function App(): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [isSiteRoutePage, setIsSiteRoutePage] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(" ");
  const [selectedValue, setSelectedValue] = useState("");
  const [tabDisplaytoggle, setTabDisplaytoggle] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<any>([]);
  const [pinndataLength, setPinnedDataLength] = useState<number>();
  const [siteDataLength, setSitesDataLength] = useState<number>();
  const [pinndataVoluntaryLength, setPinnedDataVoluntaryLength] = useState<number>();
  const [selectedSiteName, setSelectedSiteName] = useState("");
  const [listVoluntaryLength, setListVoluntaryLength] = useState<number>();
  const [visibleVoluntary, setVisibleVoluntary] = useState(false);
  const defaultHeaderInfo: HeaderInfoType = {
    selectedSite: null as unknown as SiteDropdown,
    selectedCustomer: null as unknown as CustomerDropdown,
    lastSurveyDate: null,
    nextSurveyDate: null,
    cameraSiteId: null,
  };

  const [selectedHeaderInfo, setSelectedHeaderInfo] = useState<HeaderInfoType>(() => {
    const storedInfo = localStorage.getItem("selectedHeaderInfo");
    if (!storedInfo) return defaultHeaderInfo;
    try {
      const parsed = JSON.parse(storedInfo) as HeaderInfoType;
      return parsed && typeof parsed === "object" ? parsed : defaultHeaderInfo;
    } catch {
      return defaultHeaderInfo;
    }
  });
  const dispatch = useDispatch<AppDispatch>();
  const [selectedCustomerSt, setSelectedCustomerSt] = useState<CustomerDropdown>({ name: "", code: "", customerId: "" });
  const [selectedSiteSt, setSelectedSiteSt] = useState<SiteDropdown>({ name: "", code: "", cameraSiteId: "", lastSurveyDate: "", nextSurveyDate: "", latitude: "", longitude: "" });
  const [loading, isLoading] = useState<boolean>(false);
  const [surveyData, setSurveyData] = useState<any[]>([]);
  const [vSurveyHistoryData, setVSurveyHistoryData] = useState<any[]>([]);

  const [displayUser, setDisplayUser] = useState<boolean>(false);
  const [displaySignout, setDisplaySignout] = useState<boolean>(false);


  const updateSurveyData = (data: any[]) => { setSurveyData(data) };
  const updateVSurvyeHistoryData = (data: any[]) => { setVSurveyHistoryData(data) };
  /** Sync URL `at` + localStorage on first paint so we are not blank until PermissionsProvider's effect runs. */
  const [hasToken, setHasToken] = useState<string | null>(() =>
    TokenService.bootstrapAccessTokenFromUrl()
  );

  useEffect(() => {
    const syncFromStorage = () => {
      const token = TokenService.getLocalAccessToken();
      setHasToken(token);
      if (!token) {
        console.warn("No access token found, user may not be authenticated.");
      }
    };
    syncFromStorage();
    window.addEventListener(ACCESS_TOKEN_CHANGED_EVENT, syncFromStorage);
    return () => window.removeEventListener(ACCESS_TOKEN_CHANGED_EVENT, syncFromStorage);
  }, []);

  useEffect(() => {
    // Save selectedHeaderInfo to localStorage
    localStorage.setItem("selectedHeaderInfo", JSON.stringify(selectedHeaderInfo));
  }, [selectedHeaderInfo]);

  const authStatus = useSelector((state: AppState) => state.AuraCameraCommon.isAuth);
  const voluntarySurveyList = useSelector(
    (state: AppState) => state.VoluntarySurvey.allVoluntarySurveysList
  );
  const voluntaryListFetchStatus = useSelector(
    (state: AppState) => state.VoluntarySurvey.listFetchStatus
  );
  useEffect(() => {
    void isAuth(authStatus);
  }, [authStatus]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/nifty.min.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);
  useEffect(() => {
    UserService.getUserSettings().then((resp: any) => {
      dispatch(UserSettingsReducer(resp as any));
    });
    let timerFunc: ReturnType<typeof setTimeout> | undefined;
    if (mainSiteUrl) {
      timerFunc = setTimeout(() => {
        window.location.href = mainSiteUrl;
      }, 1000 * 60 * 30);
      const maybeTimeout = timerFunc as unknown as { unref?: () => void };
      maybeTimeout.unref?.();
    }

    return () => {
      if (timerFunc) clearTimeout(timerFunc);
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const fetchPinnedSitesData = async () => {
      try {
        const data = await OverviewSitesService.getInspectionOverviewSites();
        if (cancelled) return;
        dispatch(setLeakedSites(FilterLeakedSites(data) as any));
        dispatch(SiteInfoReducer(data as any));
        setSitesDataLength(data?.length);
        const pinnedSites = data?.filter((site: any) => site.isPinned);
        setPinnedDataLength(pinnedSites?.length);
      } catch {
        if (!cancelled) {
          dispatch(setLeakedSites([] as any));
          dispatch(SiteInfoReducer([] as any));
        }
      }
    };
    void fetchPinnedSitesData();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);
  const updatePinnedDataLength = (length: number) => {
    setPinnedDataLength(length);
  };
  useEffect(() => {
    const onResize = () => {
      document.documentElement.style.setProperty(
        "--viewport-width",
        window.innerWidth + "px"
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getSelectedMenu = (menu: any) => {
    setSelectedMenu(menu);
  };
  const handleButtonClick = (value: string) => {
    setSelectedValue(value);
  };
  const updateHeaderDataFn = (rowData: any) => {
  
    const {
      siteName,
      siteId,
      customerName,
      customerId,
      lastSurveyDate,
      estimatedNextSurveyDate,
      cameraSiteId
    } = rowData;
      dispatch(setSelectedCustomerInfo({
      selectedSite: {
        name: siteName,
        code: siteId,
        cameraSiteId: cameraSiteId,
        lastSurveyDate: lastSurveyDate,
        nextSurveyDate: estimatedNextSurveyDate,
        latitude: rowData?.coordinates?.latitude,
        longitude: rowData?.coordinates?.longitude
      },
      selectedCustomer: {
        name: customerName,
        code: customerId,
        customerId: customerId
      },
      lastSurveyDate: lastSurveyDate,
      nextSurveyDate: estimatedNextSurveyDate,
      cameraSiteId: cameraSiteId
    }));
    setSelectedHeaderInfo({
      selectedSite: {
        name: siteName,
        code: siteId,
        cameraSiteId: cameraSiteId,
        lastSurveyDate: lastSurveyDate,
        nextSurveyDate: estimatedNextSurveyDate,
        latitude: rowData?.coordinates?.latitude,
        longitude: rowData?.coordinates?.longitude
      },
      selectedCustomer: {
        name: customerName,
        code: customerId,
        customerId: customerId
      },
      lastSurveyDate: lastSurveyDate,
      nextSurveyDate: estimatedNextSurveyDate,
      cameraSiteId: cameraSiteId
    });
    setSelectedCustomerSt({
      name: customerName,
      code: customerId,
      customerId: customerId
    });
    setSelectedSiteSt({
      name: siteName,
      code: siteId,
      cameraSiteId: cameraSiteId,
      lastSurveyDate: lastSurveyDate,
      nextSurveyDate: estimatedNextSurveyDate,
      latitude: rowData?.coordinates?.latitude,
      longitude: rowData?.coordinates?.longitude
    });
  };
  const updatedCustomer = (customer: CustomerDropdown) => {
    setSelectedCustomerSt(customer);
  };
  const updatedSite = (site: SiteDropdown) => {
    setSelectedSiteSt(site);
  };
  useEffect(() => {
    void dispatch(ensureAllVoluntarySurveysLoaded());
  }, [dispatch]);

  useEffect(() => {
    if (voluntaryListFetchStatus === "succeeded") {
      setListVoluntaryLength(voluntarySurveyList?.length);
      const pinnedSites = voluntarySurveyList?.filter((site: any) => site.isPinned);
      setPinnedDataVoluntaryLength(pinnedSites?.length);
    } else if (voluntaryListFetchStatus === "failed") {
      setListVoluntaryLength(undefined);
      setPinnedDataVoluntaryLength(undefined);
    }
  }, [voluntarySurveyList, voluntaryListFetchStatus]);

  const handleSelectedSiteName = (siteName: any) => {
    setSelectedSiteName(siteName);
  };
  const updateVoluntaryPinnedData = (length: number) => {
    setPinnedDataVoluntaryLength(length)
  }
  useEffect(() => {
    const savedMenu = sessionStorage.getItem('selectedMenu');
    if (savedMenu) {
      setSelectedMenu(savedMenu);
    }
  }, []);

  useEffect(() => {
    if (selectedMenu) {
      sessionStorage.setItem('selectedMenu', selectedMenu);
    }
  }, [selectedMenu]);

  useEffect(() => {
    sessionStorage.removeItem("dashboardRenderCount");
  }, []);

  const updateSignout = (isSignout: boolean) => {
    setDisplaySignout(isSignout);
  }
  const updateUser = (isUser: boolean) => {
    setDisplayUser(isUser);
  }

  return (
    <>
      <style>{MapTableStyles}</style>
      <User displayUser={displayUser} setDisplayUser={setDisplayUser} />
      <Signout displaySignout={displaySignout} setDisplaySignout={updateSignout} />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <PermissionsProvider>
          {hasToken && hasToken !== "" ? (<>
            <>
              <RouteSync setTabDisplaytoggle={setTabDisplaytoggle} setSelectedMenu={setSelectedMenu} />
              {<div className="main-app-container text-base">
                <div className="app-left-menu-bar">
                  {/* Main menubar start */}
                  <MenuSidebar updateSelectedMenu={getSelectedMenu} setDisplayUser={updateUser} setDisplaySignout={updateSignout} />
                  {/* <Slider /> */}
                  {/* Main menubar end */}
                </div>
                {/*  Body data container start */}
                <div className="app-body-content-panel" style={{ paddingLeft: "65px" }}>
                  <div className="app-right-body-container">
                    {/*  Header start */}
                    <div className="app-header p-2 pl-3 pr-3">
                      <HeaderComponent
                        updateVisible={setVisible}
                        siteRoutePage={isSiteRoutePage}
                        selectedMenu={selectedMenu}
                        selectedValue={selectedValue}
                        breadcrumb={breadcrumb}
                        selectedHeaderInfo={selectedHeaderInfo}
                        setTabDisplaytoggle={setTabDisplaytoggle}
                        updatedSite={updatedSite}
                        updatedCustomer={updatedCustomer}
                        setSurveyData={updateSurveyData}
                        selectedSiteName={selectedSiteName}
                        updateVoluntarySurvey={setVisibleVoluntary}
                        setVSurveyHistoryData={updateVSurvyeHistoryData}
                      />
                    </div>
                    {/*  Header end */}
                    <div className="line"></div>
                    {loading ? (<div className="Progress flex justify-content-center"><ProgressSpinner className="progress-spinner" /></div>) : (
                      <div style={{ position: "relative", height: "100vh" }}>
                        {/* Regulatory/Voluntary survey Tabs start */}
                        <SurveyTabsBar
                          tabDisplaytoggle={tabDisplaytoggle}
                          selectedMenu={selectedMenu}
                          setSelectedMenu={setSelectedMenu}
                          pinndataLength={pinndataLength}
                          siteDataLength={siteDataLength}
                          pinndataVoluntaryLength={pinndataVoluntaryLength}
                          listVoluntaryLength={listVoluntaryLength}
                        />
                        {/* Regulatory/Voluntary survey Tabs end */}
                        <div className="app-content-body">
                          <Suspense
                            fallback={
                              <div
                                className="Progress flex justify-content-center align-items-center"
                                style={{ minHeight: "50vh", width: "100%" }}
                              >
                                <ProgressSpinner className="progress-spinner" />
                              </div>
                            }
                          >
                            <KeyedAppRoutes
                              updateHeaderDataFn={updateHeaderDataFn}
                              getSelectedMenu={getSelectedMenu}
                              setTabDisplaytoggle={setTabDisplaytoggle}
                              selectedValue={selectedValue}
                              selectedHeaderInfo={selectedHeaderInfo}
                              updatePinnedDataLength={updatePinnedDataLength}
                              updateVoluntaryPinnedData={updateVoluntaryPinnedData}
                              handleSelectedSiteName={handleSelectedSiteName}
                            />
                          </Suspense>
                        </div>
                        <SurveySidebar
                          visible={visible}
                          onHide={() => setVisible(false)}
                          onButtonClick={handleButtonClick}
                          selectedHeaderInfo={selectedHeaderInfo}
                          surveyData={surveyData}
                        />
                      </div>
                    )}
                  </div>
                  <VSurveyHistory
                    visible={visibleVoluntary}
                    onHide={() => setVisibleVoluntary(false)}
                    selectedHeaderInfo={selectedHeaderInfo}
                    vSurveyHistoryData={vSurveyHistoryData}
                  />
                </div>
                {/*  Body data container end */}

                {/* <MenuSidebar/> */}

                {/* Menu tooltip data container start */}
                <ReactTooltip id="tooltip-aura-logo" place="right" content="AURA" />
                <ReactTooltip id="tooltip-home-menu" place="right" content="Home" />
                <ReactTooltip id="tooltip-voluntary-surveys" place="right" content="Voluntary Surveys" />
                <ReactTooltip id="tooltip-regulatory-surveys" place="right" content="Regulatory Surveys" />
                <ReactTooltip id="tooltip-reports" place="right" content="Reports" />
                <ReactTooltip id="tooltip-asset-summary" place="right" content="Asset Summary" />
                <ReactTooltip id="tooltip-user" place="right" content="User" />
                <ReactTooltip id="tooltip-signout" place="right" content="Signout" />
                <ReactTooltip id="tooltip-info" place="bottom" content="Info" />
                <ReactTooltip id="tooltip-download" place="bottom-end" content="Download" />
                <ReactTooltip id="tooltip-upload" place="bottom-end" content="Upload" />
                {/* Menu tooltip data container end */}
              </div>}
            </>
          </>) : (
            <div
              className="Progress flex flex-column justify-content-center align-items-center gap-3"
              style={{ minHeight: "100vh", width: "100%", color: "#EBECED" }}
            >
              <ProgressSpinner className="progress-spinner" />
              <span className="text-sm opacity-80">Loading session…</span>
            </div>
          )}
        </PermissionsProvider>
      </Router>
    </>
  );
}
export default App;
